import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Search, Layers } from 'lucide-react'
import { Card } from '../types'
import { getCardImageUrl } from '../utils/cardImage'
import { cardsService } from '../services/cards.service'

function MarqueeRow({ spriteUrl, reverse = false, rowClass = '' }: { spriteUrl: string, reverse?: boolean, rowClass?: string }) {
  return (
    <div className={`slider ${rowClass}`}>
      <div 
        className={`track ${reverse ? 'animate-reverse' : 'animate'}`}
      >
        <img src={spriteUrl} alt="" className="h-full w-auto object-contain flex-shrink-0" />
        <img src={spriteUrl} alt="" className="h-full w-auto object-contain flex-shrink-0" />
        <img src={spriteUrl} alt="" className="h-full w-auto object-contain flex-shrink-0" />
        <img src={spriteUrl} alt="" className="h-full w-auto object-contain flex-shrink-0" />
      </div>
    </div>
  )
}

function SearchAutocomplete() {
  const [query, setQuery] = useState('')
  const [showResults, setShowResults] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [selectedImage, setSelectedImage] = useState<string>('')
  const [filteredCards, setFilteredCards] = useState<Card[]>([])
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    if (!query || query.length < 1) {
      setFilteredCards([])
      setShowResults(false)
      return
    }
    
    const timer = setTimeout(async () => {
      setLoading(true)
      try {
        const results = await cardsService.searchCards(query, 10)
        setFilteredCards(results)
        if (results.length > 0) {
          setShowResults(true)
        } else {
          setShowResults(false)
        }
      } catch (e) {
        console.error('Search error:', e)
        setFilteredCards([])
      } finally {
        setLoading(false)
      }
    }, 200)
    
    return () => clearTimeout(timer)
  }, [query])

  useEffect(() => {
    if (selectedIndex >= 0 && filteredCards[selectedIndex]) {
      setSelectedImage(getCardImageUrl(filteredCards[selectedIndex]) || '')
    } else {
      setSelectedImage('')
    }
  }, [selectedIndex, filteredCards])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex(prev =>
        prev < filteredCards.length - 1 ? prev + 1 : prev
      )
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex(prev => prev > 0 ? prev - 1 : -1)
    } else if (e.key === 'Enter') {
      if (selectedIndex >= 0 && filteredCards[selectedIndex]) {
        handleSelectCard(filteredCards[selectedIndex])
      } else if (query.length > 0) {
        navigate(`/cards?search=${encodeURIComponent(query)}`)
        setShowResults(false)
      }
    } else if (e.key === 'Escape') {
      setShowResults(false)
      setSelectedIndex(-1)
    }
  }

  const handleSelectCard = (card: Card) => {
    setQuery(card.name)
    setShowResults(false)
    navigate(`/cards?search=${encodeURIComponent(card.name)}`)
  }

  const handleClear = () => {
    setQuery('')
    setShowResults(false)
  }

  return (
    <div className="relative max-w-xl mx-auto mb-8 md:mb-12">
      <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={26} />
      <input
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value)
          setSelectedIndex(-1)
        }}
        onFocus={() => {
          if (query.length > 0 && filteredCards.length > 0) {
            setShowResults(true)
          }
        }}
        onBlur={() => {
          setTimeout(() => setShowResults(false), 200)
        }}
        onKeyDown={handleKeyDown}
        placeholder="Busca una carta..."
        className="w-full pl-16 pr-14 py-5 md:py-6 text-lg md:text-xl bg-[#1a1e24] border border-[#2d3540] rounded-xl text-white placeholder-slate-400 focus:outline-none focus:border-amber-500/50 transition-all"
      />
      {query && (
        <button
          onClick={handleClear}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-slate-400 hover:text-white transition-colors"
        >
          ×
        </button>
      )}

      {showResults && filteredCards.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-[#1a1e24] border border-[#2d3540] rounded-xl shadow-xl overflow-hidden z-50">
          <div className="flex max-h-[480px] overflow-hidden">
            <div className="flex-1 overflow-y-auto">
              {filteredCards.map((card, i) => {
                const imageUrl = getCardImageUrl(card)
                return (
                  <button
                    key={card.id}
                    onClick={() => handleSelectCard(card)}
                    onMouseEnter={() => setSelectedIndex(i)}
                    onMouseLeave={() => setSelectedIndex(-1)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all cursor-pointer ${
                      selectedIndex === i ? 'bg-[#2d3540]' : 'hover:bg-[#2d3540]'
                    }`}
                  >
                    <div className="w-12 h-16 flex-shrink-0">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={card.name}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : (
                        <div className="w-full h-full bg-[#0f1115] rounded-lg flex items-center justify-center">
                          <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center text-xs text-[#0f1115] font-bold">
                            {card.cost}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-medium truncate">{card.name}</div>
                      <div className="text-sm text-slate-400 truncate">{card.edition}</div>
                    </div>
                    <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center bg-amber-500 rounded-lg">
                      <span className="text-xs text-[#0f1115] font-bold">{card.cost}</span>
                    </div>
                  </button>
                )
              })}
              <button
                onClick={() => navigate(`/cards?search=${encodeURIComponent(query)}`)}
                className="w-full px-5 py-3 text-amber-500 hover:bg-amber-500/10 transition-all text-sm font-medium"
              >
                Ver todos los resultados
              </button>
            </div>
            {selectedIndex >= 0 && filteredCards[selectedIndex] && (
              <div className="hidden sm:flex flex-col items-center justify-center p-4 bg-[#0f1115] border-l border-[#2d3540] w-48">
            {selectedImage ? (
                  <img
                    src={selectedImage}
                    alt={filteredCards[selectedIndex].name}
                    className="w-full h-auto object-contain"
                  />
                ) : (
                  <div className="w-full aspect-[3/4] bg-[#1a1e24] rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center text-xl text-[#0f1115] font-bold mx-auto mb-2">
                        {filteredCards[selectedIndex].cost}
                      </div>
                      <div className="text-white text-sm font-medium">{filteredCards[selectedIndex].name}</div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default function HomePage() {
  const [visibleRows, setVisibleRows] = useState(5)

  useEffect(() => {
    const updateRows = () => {
      const vh = window.visualViewport?.height || window.innerHeight
      if (vh >= 800) setVisibleRows(5)
      else if (vh >= 600) setVisibleRows(4)
      else if (vh >= 450) setVisibleRows(3)
      else if (vh >= 350) setVisibleRows(2)
      else setVisibleRows(1)
    }

    updateRows()
    window.addEventListener('resize', updateRows)
    return () => window.removeEventListener('resize', updateRows)
  }, [])

  return (
    <div className="min-h-[100dvh] bg-[#0f1115]">
      <section className="relative h-[100dvh] pt-0 px-0 overflow-hidden">
        <div className="absolute inset-0 flex flex-col">
          {visibleRows >= 1 && <MarqueeRow spriteUrl="/sprites/hero-row-1.png" rowClass={`row-${visibleRows}`} />}
          {visibleRows >= 2 && <MarqueeRow spriteUrl="/sprites/hero-row-2.png" reverse rowClass={`row-${visibleRows}`} />}
          {visibleRows >= 3 && <MarqueeRow spriteUrl="/sprites/hero-row-3.png" rowClass={`row-${visibleRows}`} />}
          {visibleRows >= 4 && <MarqueeRow spriteUrl="/sprites/hero-row-4.png" reverse rowClass={`row-${visibleRows}`} />}
          {visibleRows >= 5 && <MarqueeRow spriteUrl="/sprites/hero-row-5.png" rowClass={`row-${visibleRows}`} />}
        </div>

        <div className="absolute inset-0 bg-gradient-to-b from-[#0f1115]/90 via-[#0f1115]/70 to-[#0f1115]/90" />

        <div className="relative z-10 flex flex-col items-center justify-center h-full pointer-events-none">
          <div className="max-w-2xl mx-auto text-center pointer-events-auto px-4 md:px-6">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 md:mb-10">
              Domina el <span className="text-amber-400">Meta</span>
            </h1>

            <SearchAutocomplete />

            <div className="flex flex-col sm:flex-row justify-center items-center gap-3 md:gap-4 mt-6 md:mt-8">
              <Link
                to="/cards"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 md:px-5 md:py-2.5 bg-amber-500 hover:bg-amber-400 text-[#0f1115] font-semibold rounded-lg transition-all text-sm md:text-base w-auto min-w-[140px]"
              >
                Explorar Cartas
              </Link>
              <Link
                to="/decks"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 md:px-5 md:py-2.5 bg-[#1a1e24] hover:bg-[#2d3540] text-white font-medium rounded-lg border border-[#2d3540] transition-all text-sm md:text-base w-auto min-w-[140px]"
              >
                <Layers size={18} />
                Explorar Mazos
              </Link>
            </div>

            <div className="mt-16 md:mt-24 lg:mt-32 bg-[#1a1e24]/80 backdrop-blur-sm rounded-2xl p-5 md:p-8 border border-[#2d3540]">
              <div className="grid grid-cols-3 gap-4 md:gap-10 text-center">
                <div>
                  <div className="text-2xl md:text-4xl font-bold text-white mb-1 md:mb-2">14140</div>
                  <div className="text-xs md:text-sm text-slate-400">Cartas</div>
                </div>
                <div>
                  <div className="text-2xl md:text-4xl font-bold text-white mb-1 md:mb-2">130+</div>
                  <div className="text-xs md:text-sm text-slate-400">Ediciones</div>
                </div>
                <div>
                  <div className="text-2xl md:text-4xl font-bold text-white mb-1 md:mb-2">100%</div>
                  <div className="text-xs md:text-sm text-slate-400">Gratis</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
